import {Component, EventEmitter, Input, OnChanges, Output, ChangeDetectionStrategy} from '@angular/core';
import {RouterLink} from '@angular/router';
import {FaIconComponent} from '@fortawesome/angular-fontawesome';
import {IconDefinition} from '@fortawesome/fontawesome-svg-core';
import {
  faCircle,
  faCircleCheck,
  faCircleExclamation,
  faCircleMinus,
  faSpinnerThird
} from '@fortawesome/pro-regular-svg-icons';
import {BicButtonComponent} from '@buildinginformationcloud/bic-ui-components';
import {
  ChatMycheckSeriesDto,
  ChatMycheckSeriesItemDto,
  ChatMycheckSeriesStatus
} from '../../../services/chat/chat-api.service';

/** Eine Zeile der Statusliste – einmal je Änderung berechnet, nicht im Template. */
interface SeriesRow {
  position: number;
  name: string;
  label: string;
  icon: IconDefinition;
  iconClass: string;
  spin: boolean;
  link: string[] | null;
  issues: string[];
}

const STATUS: Record<ChatMycheckSeriesStatus, { label: string; icon: IconDefinition; iconClass: string }> = {
  PLANNED: {label: 'offen', icon: faCircle, iconClass: 'text-gray-400'},
  CREATED: {label: 'wird angelegt', icon: faSpinnerThird, iconClass: 'text-[#009999]'},
  COMPLETE: {label: 'startbereit', icon: faCircleCheck, iconClass: 'text-[#009999]'},
  INCOMPLETE: {label: 'Entwurf', icon: faCircleExclamation, iconClass: 'text-yellow-600'},
  SKIPPED: {label: 'übersprungen', icon: faCircleMinus, iconClass: 'text-red-600'},
};

/**
 * Stand einer myCheck-Serie im Quickstart (BIC-Backend#189): welche myChecks fertig sind, welche
 * Entwurf blieben (mit ihren offenen Punkten) und welcher als nächster dran ist. Die Serie läuft
 * automatisch weiter; hier lässt sie sich anhalten und fortsetzen.
 */
@Component({
  selector: 'app-mycheck-series-status',
  standalone: true,
  imports: [FaIconComponent, RouterLink, BicButtonComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './mycheck-series-status.component.html',
})
export class MycheckSeriesStatusComponent implements OnChanges {
  @Input({required: true}) series!: ChatMycheckSeriesDto;
  @Input() projectId: number | undefined;
  @Input() paused = false;
  /** Läuft gerade ein Turn? Dann gibt es nichts anzuhalten oder fortzusetzen. */
  @Input() busy = false;
  @Output() pause = new EventEmitter<void>();
  @Output() resume = new EventEmitter<void>();

  rows: SeriesRow[] = [];
  doneCount = 0;
  checkRunLink: string[] | null = null;

  ngOnChanges(): void {
    const items = this.series?.items ?? [];
    this.rows = items.map(item => this.toRow(item));
    this.doneCount = items.filter(item => item.status === 'COMPLETE' || item.status === 'INCOMPLETE'
      || item.status === 'SKIPPED').length;
    this.checkRunLink = this.series?.checkRunDefinitionId && this.projectId
      ? ['/projects', String(this.projectId), 'check-runs', 'builder', this.series.checkRunDefinitionId]
      : null;
  }

  get hasNextStep(): boolean {
    return !!this.series?.confirmed && (!!this.series.next || this.series.checkRunDue);
  }

  private toRow(item: ChatMycheckSeriesItemDto): SeriesRow {
    const status = STATUS[item.status] ?? STATUS.PLANNED;
    const editor = item.mycheckType === 'IDS' ? 'ids' : item.mycheckType === 'HYBRID' ? 'hybrid' : 'ki';
    return {
      position: item.position,
      name: item.name,
      label: status.label,
      icon: status.icon,
      iconClass: status.iconClass,
      spin: item.status === 'CREATED',
      link: item.rulesetId && this.projectId
        ? ['/projects', String(this.projectId), 'my-checks', item.rulesetId, editor]
        : null,
      issues: item.status === 'INCOMPLETE' || item.status === 'SKIPPED' ? parseIssues(item.issuesJson) : [],
    };
  }
}

/** Die offenen Punkte als lesbare Sätze; unlesbares JSON zeigt lieber nichts als einen Fehler. */
export function parseIssues(json: string | null | undefined): string[] {
  if (!json) return [];
  try {
    const parsed = JSON.parse(json);
    return Array.isArray(parsed)
      ? parsed.map(issue => typeof issue === 'string' ? issue : issue?.message).filter((m): m is string => !!m)
      : [];
  } catch {
    console.warn('chatBIC: offene Punkte eines Serienpunkts nicht lesbar', json);
    return [];
  }
}
